import site from '../../site';
import template from '../../template';
import getCurrentLocale from '../../site-locales/selectors/current-locale';
import view from '../../view';
import actionTypes from '../constants/action-types';
import getGlobal from '../selectors/global';
import setGlobalHTML from '../actions/set-global-html';

import {
  addPageNavigationItem,
  removePageNavigationItem,
  updatePageNavigationItem,
} from '../helpers/navigation-markup';
import findPagesInNavigation from '../helpers/find-pages-in-navigation';

const getValuesToSynchronize = (state) => {
  const variables = template.selectors.getVariables(state);
  if (!variables || !variables.navigation) return null;

  const { navigation } = variables;
  const { globalId, querySelector } = navigation[0];
  const globalIdValue = getGlobal(state, globalId);
  if (!globalIdValue) return null;
  const { html } = globalIdValue;

  return {
    html,
    querySelector,
    globalId,
  };
};

const synchronizeNavigationGlobal = (store) => (next) => (action) => {
  switch (action.type) {
    case site.actionTypes.CREATE_PAGE:
    case site.actionTypes.TRANSLATE_PAGE:
    case site.actionTypes.COPY_PAGE: {
      const { pageId, displayInNavigation, options = {} } = action.payload;
      const { pageIdToOverride } = options;

      next(action);

      if (!displayInNavigation || pageIdToOverride) return;

      const state = store.getState();
      const newPageId = pageIdToOverride || pageId;
      const page = site.selectors.getPage(state, newPageId);
      const stateValues = getValuesToSynchronize(state);
      if (!stateValues) return;

      const { html, querySelector, globalId } = stateValues;

      const newHTML = addPageNavigationItem(html, querySelector, page);

      store.dispatch(setGlobalHTML(globalId, newHTML));

      break;
    }

    case site.actionTypes.DELETE_PAGE: {
      const { pageId } = action.payload;
      const state = store.getState();
      const page = site.selectors.getPage(state, pageId);
      const activePageId = site.selectors.getActivePageId(state);

      next(action);

      if (page && page.displayInNavigation) {
        const stateValues = getValuesToSynchronize(state);
        if (!stateValues) break;

        const { html, globalId } = stateValues;
        const newHTML = removePageNavigationItem(html, page);

        store.dispatch(setGlobalHTML(globalId, newHTML));

        if (activePageId !== pageId) {
          store.dispatch(view.actions.forceReloadView());
        }
      }

      break;
    }

    case site.actionTypes.UPDATE_PAGE_SETTINGS: {
      const { pageId, settings, options } = action.payload;
      const { forceNagivationUpdate, overridedPage } = options;
      const prevState = store.getState();
      const prevPage = site.selectors.getPage(prevState, pageId);

      const isNavigationDisplayChanged =
        prevPage.displayInNavigation !== settings.displayInNavigation;
      const isNavigationItemTextChanged =
        settings.displayInNavigation && prevPage.name !== settings.name;

      next(action);

      const state = store.getState();
      const page = site.selectors.getPage(state, pageId);

      const stateValues = getValuesToSynchronize(state);
      if (!stateValues) break;

      const { html, querySelector, globalId } = stateValues;

      let newHTML = html;

      if (forceNagivationUpdate && prevPage.displayInNavigation) {
        newHTML = updatePageNavigationItem(html, overridedPage, page);
      } else if (isNavigationDisplayChanged) {
        if (settings?.displayInNavigation) {
          newHTML = addPageNavigationItem(html, querySelector, page);
        } else {
          newHTML = removePageNavigationItem(html, page);
        }
      } else if (isNavigationItemTextChanged) {
        newHTML = updatePageNavigationItem(html, prevPage, page);
      }

      store.dispatch(setGlobalHTML(globalId, newHTML));

      break;
    }

    case site.actionTypes.SET_HOME_PAGE: {
      const { pageId } = action.payload;
      const prevState = store.getState();
      const locale = getCurrentLocale(prevState);

      const homePage = site.selectors.getHomePage(prevState, locale);
      const nextHomePage = site.selectors.getPage(prevState, pageId);

      next(action);

      if (
        (homePage && homePage.displayInNavigation) ||
        (nextHomePage && nextHomePage.displayInNavigation)
      ) {
        const state = store.getState();

        const stateValues = getValuesToSynchronize(state);
        if (!stateValues) break;

        const { html, globalId } = stateValues;

        let newHTML = html;

        if (homePage && homePage.displayInNavigation) {
          const oldHomePage = site.selectors.getPage(state, homePage.id);
          newHTML = updatePageNavigationItem(html, homePage, oldHomePage);
        }

        if (nextHomePage && nextHomePage.displayInNavigation) {
          const newHomePage = site.selectors.getHomePage(state, locale);
          newHTML = updatePageNavigationItem(newHTML, nextHomePage, newHomePage);
        }

        store.dispatch(setGlobalHTML(globalId, newHTML));
        store.dispatch(view.actions.forceReloadView());
      }

      break;
    }

    case actionTypes.SET_GLOBAL_HTML_AND_AFFECT_HISTORY:
    case actionTypes.SET_GLOBAL_HTML: {
      const state = store.getState();
      const stateValues = getValuesToSynchronize(state);
      if (!stateValues) {
        next(action);
        break;
      }

      const { globalId } = stateValues;

      if (action.payload.globalId === globalId) {
        const pages = site.selectors.getPages(state);
        const pagesInNavigation = findPagesInNavigation(pages, action.payload.html);
        const pageIds = pagesInNavigation.map((page) => page.id);

        store.dispatch(site.actions.setPagesInNavigation(pageIds));
      }

      next(action);
      break;
    }

    default: {
      next(action);
      break;
    }
  }
};

export default synchronizeNavigationGlobal;
